import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import usePageMeta from "@/hooks/usePageMeta";
import { films } from "@/mocks/films";
import DetailHero from "@/pages/detail/components/DetailHero";
import DetailContent from "@/pages/detail/components/DetailContent";
import RoleList from "@/pages/detail/components/RoleList";
import ReviewPanel from "@/pages/detail/components/ReviewPanel";
import RelatedPanel from "@/pages/detail/components/RelatedPanel";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const film = films.find((item) => item.id === id);

  usePageMeta(
    film ? `影视详情：${film.title} - 伦理视频` : "作品未找到 - 伦理视频",
    film
      ? `《${film.title}》${film.year}年${film.region}${film.kind === "movie" ? "伦理电影" : "伦理剧集"}，导演${film.director}。包含剧情简介、角色分析、精选影评与同类型相关推荐。`
      : "你访问的作品不存在或已下线，可返回伦理视频首页浏览其他伦理影视作品。",
    film ? `${film.title},${film.genres.join(",")},伦理视频,影视详情,角色分析,影评` : "伦理视频",
  );

  const related = useMemo(() => {
    if (!film) return [];
    return films
      .filter((item) => item.id !== film.id)
      .map((item) => {
        const genreScore = item.genres.filter((genre) => film.genres.includes(genre)).length * 2;
        const directorScore = item.director === film.director ? 3 : 0;
        return { item, score: genreScore + directorScore };
      })
      .filter((entry) => entry.score > 0)
      .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
      .slice(0, 4)
      .map((entry) => entry.item);
  }, [film]);

  if (!film) {
    return (
      <SiteLayout>
        <div className="flex min-h-[60vh] w-full flex-col items-center justify-center px-4 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-3xl" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-semibold text-foreground-950">作品不存在</h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            链接可能已经失效，或者该作品不在演示数据范围内。你可以回到首页，从最新推荐或热门榜单中重新选择。
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              返回首页
            </Link>
            <Link
              to="/movies"
              className="whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
            >
              浏览伦理电影
            </Link>
          </div>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <DetailHero film={film} />

      <div className="w-full px-4 py-8 md:px-10 md:py-10">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-8">
            <DetailContent film={film} />
            <RoleList roles={film.roles} />
            <ReviewPanel reviews={film.reviews} />
          </div>

          <div className="lg:col-span-4">
            <RelatedPanel film={film} related={related} />
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}